/**
 * Invoices (features.billing): #/invoices (signed dockets to invoice, by client, job and week), #/invoices/list,
 * #/invoices/<id>, #/invoices/claims (payment claims and monthly statements) and #/invoices/settings (bank
 * details, directors only). Plus the Today card: overdue invoices by client, the Monday nudge to invoice last
 * week's dockets, and payment claims waiting on a payment schedule.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton, SkeletonLines, type ChipTone } from "../../components/States";
import { BRAND } from "../../lib/config";
import { aud, fmtDate, fmtPlainDate, localYmd, weekday } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { hashParams, useHash } from "../../lib/route";
import { useAsync } from "../../lib/useAsync";
import {
  billingToday,
  claimPdf,
  createInvoice,
  createStatement,
  customerNames,
  customersWithUnpaid,
  disputeInvoice,
  docketsToInvoice,
  getBank,
  getInvoice,
  INVOICE_STATUS_LABEL,
  invoicePdf,
  isOpen,
  listClaims,
  listInvoices,
  makePaymentClaim,
  myRole,
  owing,
  previewInvoice,
  recordPayment,
  recordSchedule,
  saveBank,
  scheduleFile,
  sendInvoice,
  sendStatement,
  unitLabel,
  voidInvoice,
  type ClaimRow,
  type InvoicePreview,
  type InvoiceRow,
  type InvoiceStatus,
  type ToInvoice,
} from "./billing-data";
import { BILLING_NAV } from "./nav";

export const billingInUse = () => Boolean(BRAND.billing);

const STATUS_TONE: Record<InvoiceStatus, ChipTone> = { issued: "outline", sent: "grey", part_paid: "accent", paid: "green", disputed: "primary", void: "primary-outline" };
const statusChip = (i: Pick<InvoiceRow, "status" | "due_date">) => {
  const overdue = isOpen(i) && i.status !== "disputed" && i.due_date < localYmd();
  return overdue ? <Chip tone="primary">Overdue</Chip> : <Chip tone={STATUS_TONE[i.status]}>{INVOICE_STATUS_LABEL[i.status]}</Chip>;
};
const weekOf = (ymd: string) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};
const lastDayOfMonth = (ymd: string) => {
  const d = new Date(`${ymd.slice(0, 7)}-01T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1, 0);
  return d.toISOString().slice(0, 10);
};
const monthLabel = (ym: string) => new Intl.DateTimeFormat("en-AU", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${ym.slice(0, 7)}-01T12:00:00Z`));

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

function Tabs({ current }: { current: string }) {
  const tabs = [
    { href: "#/invoices", key: "", label: "To invoice" },
    { href: "#/invoices/list", key: "list", label: "Invoices" },
    { href: "#/invoices/claims", key: "claims", label: "Payment claims" },
    { href: "#/invoices/settings", key: "settings", label: "Billing settings" },
  ];
  return (
    <nav className="form-actions" aria-label="Invoices sections" style={{ marginBottom: 16 }}>
      {tabs.map((t) => (
        <a key={t.key} href={t.href} className={`btn btn--sm ${current === t.key ? "btn--ink" : "btn--outline"}`} aria-current={current === t.key ? "page" : undefined}>{t.label}</a>
      ))}
    </nav>
  );
}

export function Invoices() {
  const [id] = hashParams(useHash());
  const view = id ?? "";
  let body: ReactNode;
  if (view === "list") body = <InvoiceList />;
  else if (view === "claims") body = <Claims />;
  else if (view === "settings") body = <BankSettings />;
  else if (view) body = <InvoiceView key={view} id={view} />;
  else body = <ToInvoiceView />;
  const isDetail = !["", "list", "claims", "settings"].includes(view);
  return (
    <section className="page" aria-labelledby="page-title">
      {!isDetail && (
        <>
          <PageHeader eyebrow="Accounts" title={BILLING_NAV.label} meta="Tax invoices from signed dockets, payments, and Security of Payment claims." />
          <Tabs current={view} />
        </>
      )}
      {body}
    </section>
  );
}

/* ------------------------------------------------------------------ to invoice */

interface Group { key: string; customer: string; customerId: string | null; job: ToInvoice["job"]; week: string; dockets: ToInvoice[] }

function ToInvoiceView() {
  const s = useAsync(docketsToInvoice);
  const groups = useMemo(() => {
    const m = new Map<string, Group>();
    for (const d of s.data ?? []) {
      const week = weekOf(d.shiftDate);
      const key = `${d.job.id}|${week}`;
      const g = m.get(key) ?? { key, customer: d.job.customerName ?? "No client linked", customerId: d.job.customerId, job: d.job, week, dockets: [] };
      g.dockets.push(d);
      m.set(key, g);
    }
    return [...m.values()].sort((a, b) => a.customer.localeCompare(b.customer) || a.job.reference.localeCompare(b.job.reference) || a.week.localeCompare(b.week));
  }, [s.data]);
  const byCustomer = useMemo(() => {
    const m = new Map<string, Group[]>();
    for (const g of groups) m.set(g.customer, [...(m.get(g.customer) ?? []), g]);
    return [...m.entries()];
  }, [groups]);

  if (s.loading && !s.data) return <Skeleton columns={4} />;
  if (s.error) return <ErrorState message={s.error} onRetry={s.reload} />;
  if (!groups.length) return <EmptyState icon="check" title="Nothing to invoice">Every signed docket is on an invoice. Signed dockets appear here, by job and week.</EmptyState>;
  return (
    <div className="stack">
      <p className="small muted">Invoices are weekly per job. Tick the dockets to include, check the preview, then create the invoice. An invoice made on or after the last day of the month of its latest docket is also a payment claim.</p>
      {byCustomer.map(([customer, gs]) => (
        <div key={customer} className="stack">
          <h2 className="card__title" style={{ margin: 0 }}>{customer}</h2>
          {gs.map((g) => <WeekGroup key={g.key} group={g} onDone={s.reload} />)}
        </div>
      ))}
    </div>
  );
}

function WeekGroup({ group: g, onDone }: { group: Group; onDone: () => void }) {
  const [ticked, setTicked] = useState<string[]>(g.dockets.map((d) => d.id));
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [po, setPo] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const toggle = (id: string) => {
    setPreview(null);
    setTicked((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  };

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card__head">
        <h3 className="card__title"><a href={`#/jobs/${g.job.id}`}>{g.job.reference} {g.job.title}</a><span className="sub">Week of {fmtPlainDate(g.week)}</span></h3>
      </div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {!g.customerId && <p className="notice notice--warn small" role="status">Link this job to a client before invoicing it.</p>}
        <div className="checks" role="group" aria-label="Dockets to include">
          {g.dockets.map((d) => (
            <label key={d.id} className="check">
              <input type="checkbox" checked={ticked.includes(d.id)} onChange={() => toggle(d.id)} />
              {d.number}, {fmtPlainDate(d.shiftDate)}{d.status === "disputed" ? " (disputed)" : d.status === "paper" ? " (paper)" : d.status === "corrected" ? " (corrected)" : ""}
            </label>
          ))}
        </div>
        {!preview && (
          <div className="form-actions">
            <button type="button" className="btn btn--ink btn--sm" disabled={busy || !ticked.length || !g.customerId} aria-busy={busy} onClick={() => void run(async () => setPreview(await previewInvoice(ticked)))}>
              Preview invoice
            </button>
          </div>
        )}
        {preview && (
          <>
            <PreviewLines p={preview} />
            <form
              className="form-grid"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void run(async () => {
                  const r = await createInvoice(ticked, po.trim() || null);
                  onDone();
                  window.location.hash = `#/invoices/${r.invoice.id}`;
                });
              }}
            >
              <Field label="Purchase order" hint={g.job.poRequired ? "This client needs a PO number on every invoice." : "Optional"}>
                <input className="input" value={po} required={g.job.poRequired} maxLength={60} onChange={(e) => setPo(e.target.value)} />
              </Field>
              <div className="form-actions field--wide">
                <button type="submit" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy}><Icon name="check" size={16} /> Create invoice for {aud(preview.total)}</button>
                <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => setPreview(null)}>Change dockets</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewLines({ p }: { p: Pick<InvoicePreview, "lines" | "subtotal" | "gst" | "total"> & { warnings?: string[]; ratesFrom?: string } }) {
  return (
    <>
      {(p.warnings ?? []).map((w) => <p key={w} className="notice notice--warn small" role="status">{w}</p>)}
      <div className="table-wrap table-wrap--flat">
        <table className="table table--stack">
          <thead><tr><th scope="col">Description</th><th scope="col" className="n">Quantity</th><th scope="col" className="n">Price</th><th scope="col" className="n">Amount ex GST</th></tr></thead>
          <tbody>
            {p.lines.map((l, i) => (
              <tr key={i}>
                <td data-label="Description">{l.description}</td>
                <td data-label="Quantity" className="n">{Number.isInteger(l.quantity) ? l.quantity : l.quantity.toFixed(2)} {unitLabel(l.unit, l.quantity)}</td>
                <td data-label="Price" className="n">{aud(l.unitPrice)}</td>
                <td data-label="Amount ex GST" className="n">{aud(l.amount)}</td>
              </tr>
            ))}
            <tr><td colSpan={3} data-label="">Subtotal</td><td className="n" data-label="Subtotal">{aud(p.subtotal)}</td></tr>
            <tr><td colSpan={3} data-label="">GST</td><td className="n" data-label="GST">{aud(p.gst)}</td></tr>
            <tr><td colSpan={3} data-label=""><strong>Total including GST</strong></td><td className="n" data-label="Total"><strong>{aud(p.total)}</strong></td></tr>
          </tbody>
        </table>
      </div>
      {p.ratesFrom && <p className="small muted">Priced from {p.ratesFrom}.</p>}
    </>
  );
}

/* ------------------------------------------------------------------ invoice list */

function InvoiceList() {
  const s = useAsync(async () => {
    const invoices = await listInvoices();
    return { invoices, names: await customerNames(invoices.map((i) => i.customer_id)) };
  });
  const [q, setQ] = useState("");
  const [show, setShow] = useState("open");
  const today = localYmd();
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (s.data?.invoices ?? []).filter((i) => {
      if (show === "open" && !isOpen(i)) return false;
      if (show === "overdue" && !(isOpen(i) && i.status !== "disputed" && i.due_date < today)) return false;
      if (show === "paid" && i.status !== "paid") return false;
      return !needle || [i.number, s.data!.names[i.customer_id], i.snapshot.job?.reference, i.snapshot.job?.title, i.po_number].some((x) => x?.toLowerCase().includes(needle));
    });
  }, [s.data, q, show, today]);
  return (
    <>
      <FilterBar count={s.data ? `Showing ${plural(rows.length, "invoice")}` : undefined}>
        <SearchField label="Search invoices" placeholder="Number, client, job or PO" value={q} onChange={setQ} />
        <SelectField label="Show" value={show} onChange={setShow} options={[
          { value: "open", label: "Unpaid" },
          { value: "overdue", label: "Overdue" },
          { value: "paid", label: "Paid" },
          { value: "all", label: "All invoices" },
        ]} />
      </FilterBar>
      {s.loading && !s.data && <Skeleton columns={5} />}
      {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
      {s.data && !rows.length && <EmptyState icon="inbox" title="No invoices here">{s.data.invoices.length ? "Try a different search or filter." : "Create one from To invoice."}</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead><tr><th scope="col">Invoice</th><th scope="col">Client and job</th><th scope="col">Due</th><th scope="col" className="n">Total</th><th scope="col" className="n">Owing</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.id}>
                  <td data-label="Invoice"><a className="strong" href={`#/invoices/${i.id}`}>{i.number}</a><span className="sub">{fmtPlainDate(i.issue_date)}{i.is_payment_claim ? ", payment claim" : ""}</span></td>
                  <td data-label="Client and job">{s.data!.names[i.customer_id] ?? i.snapshot.customer.legalName}<span className="sub">{i.snapshot.job ? `${i.snapshot.job.reference} ${i.snapshot.job.title}` : ""}</span></td>
                  <td data-label="Due" className="nowrap">{fmtPlainDate(i.due_date)}</td>
                  <td data-label="Total" className="n">{aud(i.total)}</td>
                  <td data-label="Owing" className="n">{i.status === "void" ? "" : aud(owing(i))}</td>
                  <td data-label="Status">{statusChip(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ one invoice */

function InvoiceView({ id }: { id: string }) {
  const s = useAsync(() => getInvoice(id), [id]);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<"" | "send" | "payment" | "dispute" | "void">("");
  const { notice, setNotice, fail, ok } = useNotice();
  const today = localYmd();

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setNotice(null);
    try {
      ok(await fn());
      setPanel("");
      s.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  if (s.loading && !s.data) return <SkeletonLines rows={6} />;
  if (s.error) return <ErrorState message={s.error} onRetry={s.reload} />;
  if (!s.data) return <EmptyState icon="warning" title="Invoice not found"><a href="#/invoices/list">All invoices</a></EmptyState>;
  const { invoice: inv, lines, payments, claim } = s.data;
  const open = isOpen(inv);
  const claimFrom = lastDayOfMonth(inv.snapshot.period?.to ?? inv.issue_date);

  return (
    <div className="stack">
      <p className="small" style={{ margin: 0 }}><a href="#/invoices/list">All invoices</a></p>
      <PageHeader
        eyebrow="Invoices"
        title={`Tax invoice ${inv.number}`}
        meta={<>{statusChip(inv)} {inv.snapshot.customer.legalName}{inv.snapshot.job ? `, ${inv.snapshot.job.reference} ${inv.snapshot.job.title}` : ""}</>}
        actions={<button type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => invoicePdf(inv.id))}><Icon name="document" size={16} /> View PDF</button>}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {inv.status === "void" && <p className="notice notice--warn" role="status">Void: {inv.void_reason}</p>}
      {inv.status === "disputed" && <p className="notice notice--warn" role="status">Disputed: {inv.dispute_note}</p>}

      <div className="card">
        <div className="card__body">
          <dl className="form-grid" style={{ margin: 0 }}>
            <div><dt className="field__label">Issued</dt><dd>{fmtPlainDate(inv.issue_date)}</dd></div>
            <div><dt className="field__label">Due</dt><dd>{fmtPlainDate(inv.due_date)}{open && inv.due_date < today ? <span className="sub sub--primary">Overdue</span> : null}</dd></div>
            <div><dt className="field__label">Total</dt><dd>{aud(inv.total)}<span className="sub">{inv.amount_paid > 0 ? `${aud(inv.amount_paid)} paid, ${aud(owing(inv))} owing` : "including GST"}</span></dd></div>
            <div><dt className="field__label">Purchase order</dt><dd>{inv.po_number ?? <span className="muted">None</span>}</dd></div>
            <div><dt className="field__label">Sent</dt><dd>{inv.sent_at ? <>{fmtDate(inv.sent_at)}<span className="sub">{inv.sent_to?.join(", ")}</span></> : <span className="muted">Not sent</span>}</dd></div>
            <div><dt className="field__label">Dockets</dt><dd>{inv.snapshot.dockets.join(", ")}</dd></div>
            {BRAND.xero && <div><dt className="field__label">Xero</dt><dd>{inv.xero_invoice_id ? "Created in Xero" : <span className="muted">Not in Xero yet</span>}</dd></div>}
            {inv.reminder_sent_at && <div><dt className="field__label">Reminder</dt><dd>Sent {fmtDate(inv.reminder_sent_at)}</dd></div>}
          </dl>
        </div>
      </div>

      {inv.status !== "void" && (
        <div className="form-actions">
          {open && <button type="button" className="btn btn--primary btn--sm" onClick={() => setPanel("send")}><Icon name="mail" size={16} /> {inv.sent_at ? "Send again" : "Send to client"}</button>}
          {open && <button type="button" className="btn btn--ink btn--sm" onClick={() => setPanel("payment")}>Record payment</button>}
          {open && inv.status !== "disputed" && <button type="button" className="btn btn--outline btn--sm" onClick={() => setPanel("dispute")}>Mark disputed</button>}
          {!inv.is_payment_claim && open && today >= claimFrom && (
            <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={() => void run(async () => (await makePaymentClaim(inv.id), "This invoice is now a payment claim. Send it to serve it."))}>Make it a payment claim</button>
          )}
          {inv.amount_paid === 0 && <button type="button" className="btn btn--outline btn--sm" onClick={() => setPanel("void")}>Void</button>}
        </div>
      )}
      {panel === "send" && <SendPanel inv={inv} busy={busy} onCancel={() => setPanel("")} onSend={(to) => run(async () => {
        const r = await sendInvoice(inv.id, to);
        return `Sent to ${r.invoice.sentTo.join(", ")}.${r.xero.created ? " Created in Xero." : r.xero.error ? ` Xero: ${r.xero.error}` : ""}${inv.is_payment_claim && !claim?.served_at ? " The payment claim is served." : ""}`;
      })} />}
      {panel === "payment" && <PaymentPanel max={owing(inv)} busy={busy} onCancel={() => setPanel("")} onSave={(amount, paidOn, ref) => run(async () => (await recordPayment(inv.id, amount, paidOn, ref), "Payment recorded."))} />}
      {panel === "dispute" && <ReasonPanel label="What is disputed" action="Mark disputed" busy={busy} onCancel={() => setPanel("")} onSave={(note) => run(async () => (await disputeInvoice(inv.id, note), "Marked disputed. Reminders stop until it is resolved."))} />}
      {panel === "void" && <ReasonPanel label="Why it is void" hint="Voiding is final. The dockets stay billed by this invoice: raise a new invoice by hand if the work is still to be charged." action="Void invoice" busy={busy} onCancel={() => setPanel("")} onSave={(reason) => run(async () => (await voidInvoice(inv.id, reason), "Invoice voided."))} />}

      <div className="card">
        <div className="card__head"><h2 className="card__title">Charges</h2></div>
        <div className="card__body"><PreviewLines p={{ lines, subtotal: inv.subtotal, gst: inv.gst, total: inv.total, ratesFrom: inv.snapshot.ratesFrom }} /></div>
      </div>

      {payments.length > 0 && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Payments</h2></div>
          <ul className="list">
            {payments.map((p) => (
              <li key={p.id} className="list__row"><span>{fmtPlainDate(p.paid_on)}<span className="sub">{p.source === "xero" ? "From Xero" : "Recorded by the office"}{p.reference ? `, ${p.reference}` : ""}</span></span><span className="list__value">{aud(p.amount)}</span></li>
            ))}
          </ul>
        </div>
      )}

      {claim && <ClaimCard claim={claim} onChange={s.reload} />}
    </div>
  );
}

function SendPanel({ inv, busy, onCancel, onSend }: { inv: InvoiceRow; busy: boolean; onCancel: () => void; onSend: (to: string[] | undefined) => void }) {
  const [to, setTo] = useState((inv.sent_to?.length ? inv.sent_to : [inv.snapshot.customer.email ?? ""]).join(", "));
  return (
    <form className="card card__body form-grid" onSubmit={(e) => { e.preventDefault(); onSend(to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean)); }}>
      <Field label="Send to" hint="The client's accounts email. Separate more than one with commas." wide>
        <input className="input" type="text" inputMode="email" required value={to} onChange={(e) => setTo(e.target.value)} />
      </Field>
      {inv.is_payment_claim && <p className="small muted field--wide">This invoice is a payment claim. Sending it serves the claim and starts the payment schedule clock.</p>}
      <div className="form-actions field--wide">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy}>Send invoice</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function PaymentPanel({ max, busy, onCancel, onSave }: { max: number; busy: boolean; onCancel: () => void; onSave: (amount: number, paidOn: string, reference: string | null) => void }) {
  const [amount, setAmount] = useState(String(max));
  const [paidOn, setPaidOn] = useState(localYmd());
  const [ref, setRef] = useState("");
  return (
    <form className="card card__body form-grid" onSubmit={(e) => { e.preventDefault(); onSave(Number(amount), paidOn, ref.trim() || null); }}>
      <Field label="Amount" hint={`${aud(max)} owing`}><input className="input" type="number" min={0.01} max={max} step={0.01} required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Paid on"><input className="input" type="date" required value={paidOn} max={localYmd()} onChange={(e) => setPaidOn(e.target.value)} /></Field>
      <Field label="Reference" hint="Optional, such as the bank reference"><input className="input" maxLength={120} value={ref} onChange={(e) => setRef(e.target.value)} /></Field>
      <div className="form-actions field--wide">
        <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Record payment</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ReasonPanel({ label, hint, action, busy, onCancel, onSave }: { label: string; hint?: string; action: string; busy: boolean; onCancel: () => void; onSave: (text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <form className="card card__body form-grid" onSubmit={(e) => { e.preventDefault(); onSave(text); }}>
      <Field label={label} hint={hint} wide><textarea className="input" rows={3} required minLength={5} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <div className="form-actions field--wide">
        <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>{action}</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ payment claims */

function ClaimCard({ claim: c, onChange }: { claim: ClaimRow; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const schedule = c.payment_schedules?.[0];
  const today = localYmd();
  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setNotice(null);
    try {
      ok(await fn());
      setRecord(false);
      onChange();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">{c.kind === "statement" ? `Payment claim for ${monthLabel(c.claim_month)}` : "Payment claim"}</h2></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <p className="small">{c.construction_work}. Claimed {aud(c.claimed_amount)} including GST.</p>
        {!c.served_at && <p className="small muted">Not served yet. {c.kind === "statement" ? "Send it to serve it." : "Sending the invoice serves it."}</p>}
        {c.served_at && (
          <p className="small">
            Served {fmtDate(c.served_at)} to {c.served_to?.join(", ")}. Payment schedule due by {fmtPlainDate(c.schedule_due_date!)}.
            {!schedule && c.schedule_due_date! < today && <span className="sub sub--primary">{BRAND.billing?.alerts.noSchedule.replace("{customer}", "The client").replace("{business}", BRAND.name)}</span>}
          </p>
        )}
        {schedule ? (
          <p className="small">
            Payment schedule received {fmtPlainDate(schedule.received_on)}: {aud(schedule.scheduled_amount)}.{schedule.reasons ? ` ${schedule.reasons}` : ""}
            {schedule.file_name && <> <button type="button" className="btn btn--outline btn--xs" onClick={() => void openBlobInNewTab(() => scheduleFile(c.id))}>View</button></>}
          </p>
        ) : (
          c.served_at && !record && <div className="form-actions"><button type="button" className="btn btn--outline btn--sm" onClick={() => setRecord(true)}>Record payment schedule</button></div>
        )}
        {record && <ScheduleForm busy={busy} max={c.claimed_amount} onCancel={() => setRecord(false)} onSave={(v) => run(async () => (await recordSchedule(c.id, v), "Payment schedule recorded."))} />}
        <div className="form-actions">
          {c.kind === "statement" && <button type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => claimPdf(c.id))} disabled={!c.served_at}>View PDF</button>}
          {c.kind === "statement" && !c.served_at && <button type="button" className="btn btn--primary btn--sm" disabled={busy} aria-busy={busy} onClick={() => void run(async () => (await sendStatement(c.id), "Payment claim sent and served."))}>Send and serve</button>}
        </div>
      </div>
    </div>
  );
}

function ScheduleForm({ busy, max, onCancel, onSave }: { busy: boolean; max: number; onCancel: () => void; onSave: (v: { scheduledAmount: number; receivedOn: string; reasons: string; file: File | null }) => void }) {
  const [amount, setAmount] = useState(String(max));
  const [receivedOn, setReceivedOn] = useState(localYmd());
  const [reasons, setReasons] = useState("");
  const [file, setFile] = useState<File | null>(null);
  return (
    <form className="form-grid" onSubmit={(e) => { e.preventDefault(); onSave({ scheduledAmount: Number(amount), receivedOn, reasons, file }); }}>
      <Field label="Amount they will pay"><input className="input" type="number" min={0} step={0.01} required value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <Field label="Received on"><input className="input" type="date" required value={receivedOn} max={localYmd()} onChange={(e) => setReceivedOn(e.target.value)} /></Field>
      <Field label="Reasons for paying less" wide><textarea className="input" rows={2} maxLength={4000} value={reasons} onChange={(e) => setReasons(e.target.value)} /></Field>
      <Field label="The schedule" hint="PDF, JPEG or PNG, optional"><input className="input" type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
      <div className="form-actions field--wide">
        <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Save</button>
        <button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Claims() {
  const s = useAsync(async () => {
    const claims = await listClaims();
    return { claims, names: await customerNames(claims.map((c) => c.customer_id)), customers: await customersWithUnpaid() };
  });
  const lastMonth = (() => {
    const d = new Date(`${localYmd().slice(0, 7)}-01T12:00:00Z`);
    const today = localYmd();
    if (today < lastDayOfMonth(today)) d.setUTCMonth(d.getUTCMonth() - 1);
    return d.toISOString().slice(0, 7);
  })();
  const [customerId, setCustomerId] = useState("");
  const [month, setMonth] = useState(lastMonth);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  if (s.loading && !s.data) return <Skeleton columns={4} />;
  if (s.error) return <ErrorState message={s.error} onRetry={s.reload} />;
  const { claims, names, customers } = s.data!;
  return (
    <div className="stack">
      <div className="card">
        <div className="card__head"><h2 className="card__title">Monthly statement</h2></div>
        <form
          className="card__body form-grid"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setNotice(null);
            createStatement(customerId, month)
              .then((r) => {
                ok(`Payment claim for ${aud(r.claim.claimedAmount)} made. Send it below to serve it.`);
                s.reload();
              })
              .catch(fail)
              .finally(() => setBusy(false));
          }}
        >
          <p className="small muted field--wide">A payment claim of a client's unpaid invoices for a month and before. It can be made from the last day of the month.</p>
          <div className="field--wide"><Notice notice={notice} onDismiss={() => setNotice(null)} /></div>
          <Field label="Client">
            <select className="input" required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">Choose a client</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Month"><input className="input" type="month" required value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy || !customerId} aria-busy={busy}>Make payment claim</button></div>
        </form>
      </div>
      {!claims.length && <EmptyState icon="inbox" title="No payment claims yet">An invoice issued on or after the last day of the month of its work is a payment claim. Monthly statements appear here too.</EmptyState>}
      {claims.map((c) => (
        <div key={c.id}>
          <p className="small strong" style={{ margin: "0 0 6px" }}>
            {names[c.customer_id] ?? "Client"}{c.invoice_id ? <>, <a href={`#/invoices/${c.invoice_id}`}>open the invoice</a></> : null}
          </p>
          <ClaimCard claim={c} onChange={s.reload} />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ bank details */

function BankSettings() {
  const s = useAsync(async () => ({ role: await myRole(), bank: await getBank() }));
  const [f, setF] = useState<{ accountName: string; bsb: string; accountNumber: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  if (s.loading && !s.data) return <SkeletonLines rows={3} />;
  if (s.error) return <ErrorState message={s.error} onRetry={s.reload} />;
  if (s.data!.role !== "director") return <EmptyState icon="warning" title="Directors only">Only a director can see or change the bank details printed on invoices.</EmptyState>;
  const b = s.data!.bank;
  const form = f ?? { accountName: b?.bank_account_name ?? "", bsb: b?.bank_bsb ?? "", accountNumber: b?.bank_account_number ?? "" };
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setF({ ...form, [k]: e.target.value });
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">Bank details on invoices</h2></div>
      <form
        className="card__body form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setNotice(null);
          saveBank({ accountName: form.accountName.trim(), bsb: form.bsb.trim(), accountNumber: form.accountNumber.replace(/\s/g, "") })
            .then(() => {
              ok("Bank details saved. New invoices print them.");
              setF(null);
              s.reload();
            })
            .catch(fail)
            .finally(() => setBusy(false));
        }}
      >
        <p className="small muted field--wide">Printed on each new invoice under How to pay. Invoices already made keep the details they were made with. Only directors can see this.</p>
        <div className="field--wide"><Notice notice={notice} onDismiss={() => setNotice(null)} /></div>
        {!b && <p className="notice notice--warn small field--wide" role="status">No bank details yet. Invoices say to contact you for payment details until they are added.</p>}
        <Field label="Account name"><input className="input" required maxLength={120} value={form.accountName} onChange={set("accountName")} autoComplete="off" /></Field>
        <Field label="BSB" hint="6 digits"><input className="input" required inputMode="numeric" pattern="\d{3}-?\d{3}" value={form.bsb} onChange={set("bsb")} autoComplete="off" /></Field>
        <Field label="Account number"><input className="input" required inputMode="numeric" pattern="[\d ]{5,12}" value={form.accountNumber} onChange={set("accountNumber")} autoComplete="off" /></Field>
        <div className="form-actions field--wide">
          <button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Save bank details</button>
          {b?.updated_at && <span className="small muted">Last changed {fmtDate(b.updated_at)}</span>}
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ Today */

export function BillingTodayCard() {
  const s = useAsync(billingToday);
  const monday = weekday(localYmd()) === 1;
  const d = s.data;
  if (s.loading && !d) return null;
  if (s.error) return <div className="card card--alert"><div className="card__head"><h2 className="card__title">Invoices</h2></div><p className="small sub--primary" style={{ margin: "0 16px 12px" }}>{s.error}</p></div>;
  if (!d || (!d.overdue.length && !d.awaitingSchedule.length && !(monday && d.toInvoice))) return null;
  return (
    <div className="card card--alert">
      <div className="card__head">
        <h2 className="card__title">Invoices</h2>
        <a className="card__link" href="#/invoices">Open invoices <Icon name="arrow" size={16} /></a>
      </div>
      <ul className="list">
        {monday && d.toInvoice > 0 && (
          <li className="list__row"><span><a className="strong" href="#/invoices">Invoice last week</a><span className="sub">{plural(d.toInvoice, "signed docket")} on {plural(d.toInvoiceJobs, "job")} to invoice</span></span></li>
        )}
        {d.overdue.map((o) => (
          <li key={o.customer} className="list__row"><span><a className="strong" href="#/invoices/list">{o.customer}</a><span className="sub">{plural(o.count, "overdue invoice")}, oldest due {fmtPlainDate(o.oldest)}</span></span><span className="list__value list__value--primary">{aud(o.owing)}</span></li>
        ))}
        {d.awaitingSchedule.map((c) => (
          <li key={c.id} className="list__row"><span><a className="strong" href={c.invoice_id ? `#/invoices/${c.invoice_id}` : "#/invoices/claims"}>{d.names[c.customer_id] ?? "Client"}</a><span className="sub">Payment schedule due by {fmtPlainDate(c.schedule_due_date!)}</span></span><span className="list__value">{aud(c.claimed_amount)}</span></li>
        ))}
      </ul>
    </div>
  );
}
